import React, { useState } from "react";
import { Button, FormControl, MenuItem, TextField } from "@mui/material";
import logoImage from "../assets/images/logo.png";
import { User } from '../aws';
import { v4 as uuid } from "uuid";

/**Expected parameters for a UserEntryPage.
 * @param {function} onFormSubmitted - Called when the form is submitted.
*/
interface UserEntryProps {
    onFormSubmitted: (userParams: User) => void
}

/**The entry page for the user's personal data. This appears before the game starts. Required fields must be filled before proceeding to the game.*/
export const UserEntryPage = (props: UserEntryProps) => {
    const { onFormSubmitted } = props;
    const initialValues = {
        sessionId: uuid(),
        nameFirst: "",
        nameLast: "",
        email: "",
        profession: "",
        interest: "",
    };
    //if any of these fields are true, the corresponding field will display an errror status
    const initialFieldErrorStatus = {
        nameFirst: false,
        nameLast: false,
        email: false
    };
    const availableProfessions = ["Primary care physician","Pulmonologist","Patient/caregiver","Other"];
    const [formData, setFormData] = useState(initialValues);
    const [fieldErrorStatus, setErrorStatus] = useState(initialFieldErrorStatus);

    const onSubmitPressed = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const areRequiredFieldsValid = Object.values(fieldErrorStatus).every(item => !item);
        if (!areRequiredFieldsValid) return;
        const userParams = formData as User;
        onFormSubmitted(userParams);
    }

    const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({...formData, [name]: value.trim()});
        setErrorStatus({...fieldErrorStatus, [name]: updateErrorStatus(name, value)});
    }

    const updateErrorStatus = (fieldName: string, value: string) => {
        switch (fieldName) {
            case "nameFirst":
            case "nameLast":
                return /[`!@#$%^&*()_+=[\]{};':"\\|,.<>/?~\d]/.test(value);
            case "email":
                return !/^[a-zA-Z0-9#!%$'&+*/=?^_`.{|}~-]+@[a-zA-Z-]+?\.[a-zA-Z]+$/.test(value);
        }
    }

    return (
        <div className="user-entry-page">
            <h1>Bridging Specialties™</h1>
            <h3>A Patient`s Journey to ILD Diagnosis.</h3>
            <img id="entry-logo" src={logoImage} alt=""/>
            <div className="user-entry-wrapper">
                <sub>* fields are required.</sub>
                <form onSubmit={onSubmitPressed}>
                    <FormControl fullWidth>
                        <div className="entry">
                            <TextField
                            fullWidth
                            className="entry"
                            label="First Name"
                            name="nameFirst"
                            id="name-first"
                            variant="filled"
                            onChange={onInputChanged}
                            required
                            defaultValue={""}
                            error={fieldErrorStatus.nameFirst}
                            />
                        </div>
                        <div className="entry">
                            <TextField
                            fullWidth
                            className="entry"
                            label="Last Name"
                            name="nameLast"
                            id="name-last"
                            variant="filled"
                            onChange={onInputChanged}
                            required
                            defaultValue={""}
                            error={fieldErrorStatus.nameLast}
                            />
                        </div>
                        <div className="entry">
                            <TextField
                            type='email'
                            fullWidth
                            label="Email"
                            name="email"
                            id="email"
                            variant="filled"
                            onChange={onInputChanged}
                            required
                            defaultValue={""}
                            error={fieldErrorStatus.email}
                            />
                        </div>
                        <div className="entry">
                            <TextField
                            fullWidth
                            label="I am a..."
                            name="profession"
                            id="profession"
                            select
                            defaultValue={""}
                            onChange={onInputChanged}
                            >
                                {availableProfessions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                        <div className="entry">
                            <TextField
                            fullWidth
                            name="interest"
                            id="interest"
                            placeholder="Please share why you're interested in Bridging Specialties™"
                            multiline
                            rows={4}
                            variant="standard"
                            onChange={onInputChanged}
                            />
                        </div>
                    </FormControl>
                    <div className="entry-button">
                        <Button variant="contained" color="inherit" type="submit">
                            START GAME
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}